import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { Mail, Lock, ArrowRight, Loader2, AlertTriangle, ShieldAlert, MessageCircle, Eye, EyeOff } from 'lucide-react';

import { useLogin } from '../../../features/auth/useLogin.ts';
import TurnstileWidget from '../../../components/TurnstileWidget.jsx';

export function LoginForm() {
  const [attemptsLeft, setAttemptsLeft] = useState(null);
  const [isLocked, setIsLocked] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
  });
  
  const login = useLogin();

  const onSubmit = (values) => {
    setAttemptsLeft(null);
    setIsLocked(false);
    
    // Agregar el token de Turnstile al payload
    const payload = {
      ...values,
      turnstile_token: turnstileToken,
    };
    
    login.mutate(payload, {
      onError: (error) => {
        const errorData = error.response?.data;
        
        // Verificar si hay attempts_left en la respuesta
        if (errorData?.attempts_left !== undefined) {
          setAttemptsLeft(errorData.attempts_left);
          
          // Si attempts_left es 0, está bloqueado
          if (errorData.attempts_left === 0) {
            setIsLocked(true);
          }
        }
      }
    });
  };

  const groupClass = "space-y-1.5"; 
  const labelClass = "block text-[9px] font-bold uppercase tracking-widest text-white/40 ml-1";
  const inputContainerClass = "relative group";
  const inputClass = "w-full h-10 bg-black/40 border border-white/10 rounded-lg pl-10 pr-4 text-sm text-white placeholder-white/20 focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/20 transition-all";
  const iconClass = "absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30 group-focus-within:text-indigo-400 transition-colors";
  const errorClass = "text-[10px] text-red-400 mt-0.5 ml-1";

  return (
    <>
      {/* Modal de cuenta bloqueada */}
      {isLocked && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-red-500/30 rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-500/10 rounded-full">
                <ShieldAlert className="h-6 w-6 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-white">Cuenta Bloqueada</h3>
            </div>
            
            <p className="text-slate-300 mb-6">
              Tu cuenta ha sido bloqueada temporalmente por múltiples intentos fallidos de inicio de sesión.
            </p>
            
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
              <p className="text-sm text-slate-200">
                <strong>¿Necesitas ayuda?</strong><br />
                Contáctanos para desbloquear tu cuenta:
              </p>
            </div>
            
            <div className="flex gap-3">
              <a
                href="https://t.me/TU_USUARIO_TELEGRAM"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm transition-all"
              >
                <MessageCircle className="h-4 w-4" />
                Telegram
              </a>
              <a
                href="https://wa.me/TU_NUMERO_WHATSAPP"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-green-600 hover:bg-green-500 text-white font-bold text-sm transition-all"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp
              </a>
            </div>
            
            <button
              onClick={() => setIsLocked(false)}
              className="w-full mt-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Advertencia de intentos restantes */}
        {attemptsLeft !== null && attemptsLeft > 0 && (
          <div className={`border rounded-lg p-3 ${
            attemptsLeft === 1 
              ? 'bg-red-500/10 border-red-500/30' 
              : 'bg-yellow-500/10 border-yellow-500/30'
          }`}>
            <div className="flex items-start gap-2">
              <AlertTriangle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                attemptsLeft === 1 ? 'text-red-500' : 'text-yellow-500'
              }`} />
              <div className="flex-1">
                <p className={`text-sm font-semibold ${
                  attemptsLeft === 1 ? 'text-red-400' : 'text-yellow-400'
                }`}>
                  {attemptsLeft === 1 
                    ? '⚠️ Último intento disponible' 
                    : `Te quedan ${attemptsLeft} intentos`}
                </p>
                {attemptsLeft === 1 && (
                  <p className="text-xs text-slate-300 mt-1">
                    Si no recuerdas tu contraseña, puedes{' '}
                    <Link 
                      to="/recuperar-password" 
                      className="text-indigo-400 hover:text-indigo-300 underline font-semibold"
                    >
                      cambiarla aquí
                    </Link>
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
        
        <div className={groupClass}>
          <label htmlFor="email-input" className={labelClass}>Correo electrónico</label>
          <div className={inputContainerClass}>
            <Mail className={iconClass} aria-hidden="true" />
            <input
              id="email-input"
              type="email"
              placeholder="tu@email.com"
              className={inputClass}
              aria-label="Correo electrónico"
              aria-required="true"
              aria-invalid={errors.email ? "true" : "false"}
              {...register('email', { required: 'Requerido' })}
            />
          </div>
          {errors.email && <p className={errorClass} role="alert">{errors.email.message}</p>}
        </div>

        <div className={groupClass}>
          <label htmlFor="password-input" className={labelClass}>Contraseña</label>
          <div className={inputContainerClass}>
            <Lock className={iconClass} aria-hidden="true" />
            <input
              id="password-input"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              className={inputClass}
              aria-label="Contraseña"
              aria-required="true"
              aria-invalid={errors.password ? "true" : "false"}
              {...register('password', { required: 'Requerido' })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors focus:outline-none"
              aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          {errors.password && <p className={errorClass} role="alert">{errors.password.message}</p>}
        </div>

        {/* Widget de Turnstile (Captcha) */}
        <TurnstileWidget
          onVerify={(token) => setTurnstileToken(token)}
          onError={() => setTurnstileToken(null)}
          onExpire={() => setTurnstileToken(null)}
        />

        <button
          type="submit"
          disabled={login.isPending}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white font-bold uppercase tracking-widest text-[10px] shadow-[0_0_15px_rgba(99,102,241,0.3)] transition-all transform hover:scale-[1.02] active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed border border-white/10 mt-2"
        >
          {login.isPending ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin" /> Verificando...
            </>
          ) : (
            <>
              Ingresar <ArrowRight className="h-3 w-3" />
            </>
          )}
        </button>
      </form>
    </>
  );
}